// src/layouts/LeaderLayout.js - frame of the leader area: top bar, menu, content.
// Audit FL-34: labelled menu button with aria-expanded, Esc closes the phone menu,
// the menu closes after navigation, sticky bar and sidebar, no extra page scroll,
// skip link, content can shrink (no horizontal scroll on phones). Renders the nested
// route (<Outlet/>) when used as a layout route, or its children.
import React, { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Menu } from 'lucide-react';
import LeaderSidebar from '../components/layout/LeaderSidebar';
import Navbar from '../components/layout/Navbar';

export const LeaderLayout = ({ children }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isMenuOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setIsMenuOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isMenuOpen]);

  return (
    <div className="min-h-screen bg-gray-100 print:min-h-0 print:bg-white">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-2 focus:top-2 focus:z-[70] focus:rounded-md focus:bg-white focus:px-4 focus:py-2 focus:shadow"
      >
        Přeskočit na obsah
      </a>

      <div className="sticky top-0 z-30 print:hidden">
        <Navbar>
          <button
            type="button"
            onClick={() => setIsMenuOpen((open) => !open)}
            aria-label={isMenuOpen ? 'Zavřít menu' : 'Otevřít menu'}
            aria-expanded={isMenuOpen}
            aria-controls="leader-mobile-menu"
            className="rounded-md p-2 text-gray-600 hover:bg-gray-100 hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 md:hidden"
          >
            <Menu className="h-6 w-6" aria-hidden="true" />
          </button>
        </Navbar>
      </div>

      <div className="flex">
        <aside className="hidden flex-shrink-0 print:hidden md:sticky md:top-16 md:block md:h-[calc(100vh-4rem)]">
          <LeaderSidebar />
        </aside>

        {isMenuOpen && (
          <div id="leader-mobile-menu" className="fixed inset-0 z-40 md:hidden print:hidden" role="dialog" aria-modal="true" aria-label="Menu">
            <div className="absolute inset-0 bg-gray-900/50" onClick={() => setIsMenuOpen(false)} aria-hidden="true" />
            <div className="relative h-full w-64 max-w-[85vw]">
              <LeaderSidebar onClose={() => setIsMenuOpen(false)} />
            </div>
          </div>
        )}

        <main id="main-content" tabIndex={-1} className="min-w-0 flex-1 p-4 focus:outline-none sm:p-6 print:m-0 print:p-0">
          <div className="mx-auto max-w-7xl print:m-0 print:max-w-none">{children === undefined ? <Outlet /> : children}</div>
        </main>
      </div>
    </div>
  );
};
